import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Wallet, CheckCircle, SpinnerGap, Eye, EyeSlash, ClipboardText } from "@phosphor-icons/react";
import api from "../lib/api";

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionIdFromUrl = searchParams.get("session_id");
  const navigate = useNavigate();
  const { register } = useAuth();

  const [sessionId, setSessionId] = useState(sessionIdFromUrl || "");
  const [paymentStatus, setPaymentStatus] = useState(sessionIdFromUrl ? "checking" : "no_session");
  const [pollCount, setPollCount] = useState(0);

  // Registration form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const pollPaymentStatus = useCallback(async (sid) => {
    const idToCheck = sid || sessionId;
    if (!idToCheck) return;
    try {
      const response = await api.get(`/checkout/status/${idToCheck}`);
      const data = response.data;
      if (data.payment_status === "paid") {
        setPaymentStatus("paid");
        setSessionId(idToCheck);
      } else if (data.status === "expired") {
        setPaymentStatus("expired");
      } else {
        setPaymentStatus("pending");
      }
    } catch (err) {
      // On first errors, keep trying (Stripe may take a moment)
      if (pollCount < 5) {
        setPaymentStatus("pending");
      } else {
        setPaymentStatus("error");
      }
    }
  }, [sessionId, pollCount]);

  useEffect(() => {
    if (sessionIdFromUrl) {
      pollPaymentStatus(sessionIdFromUrl);
    }
  }, [sessionIdFromUrl, pollPaymentStatus]);

  useEffect(() => {
    if (paymentStatus === "pending" || paymentStatus === "checking") {
      if (pollCount >= 15) {
        setPaymentStatus("timeout");
        return;
      }
      const timer = setTimeout(() => {
        setPollCount((c) => c + 1);
        pollPaymentStatus();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [paymentStatus, pollCount, pollPaymentStatus]);

  const handleManualCheck = async (e) => {
    e.preventDefault();
    if (!sessionId.trim()) {
      toast.error("Cole o ID da sessão de pagamento");
      return;
    }
    setPaymentStatus("checking");
    setPollCount(0);
    pollPaymentStatus(sessionId.trim());
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name, sessionId);
      toast.success("Conta criada com sucesso! Bem-vindo!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  // State: no session_id - show manual entry form
  if (paymentStatus === "no_session") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background noise-bg p-4">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
              <Wallet className="w-6 h-6 text-primary-foreground" weight="fill" />
            </div>
            <span className="font-heading font-extrabold text-2xl tracking-tight text-primary">
              Financeiro
            </span>
          </div>

          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
            Ativar sua conta
          </h1>
          <p className="text-muted-foreground mb-6">
            Se você já fez o pagamento, cole abaixo o ID da sessão que aparece no comprovante do Stripe.
          </p>

          <form onSubmit={handleManualCheck} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="session_id">ID da Sessão (começa com cs_)</Label>
              <div className="relative">
                <Input
                  id="session_id"
                  type="text"
                  placeholder="cs_live_..."
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                  required
                  className="h-12 pr-10"
                  data-testid="manual-session-input"
                />
                <ClipboardText size={20} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              </div>
            </div>
            <Button type="submit" className="w-full h-12" data-testid="verify-payment-btn">
              Verificar Pagamento
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Voltar ao Início
            </button>
          </div>
        </div>
      </div>
    );
  }

  // State: checking/pending
  if (paymentStatus === "checking" || paymentStatus === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background noise-bg">
        <div className="text-center animate-fade-in max-w-md px-4">
          <SpinnerGap size={48} className="text-primary mx-auto mb-4 animate-spin" />
          <h2 className="font-heading text-2xl font-bold mb-2">Verificando pagamento...</h2>
          <p className="text-muted-foreground">Aguarde enquanto confirmamos seu pagamento.</p>
        </div>
      </div>
    );
  }

  // State: error/expired/timeout
  if (paymentStatus === "expired" || paymentStatus === "error" || paymentStatus === "timeout") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background noise-bg">
        <div className="text-center animate-fade-in max-w-md px-4">
          <h2 className="font-heading text-2xl font-bold mb-2 text-destructive">
            {paymentStatus === "expired" ? "Sessão expirada" : paymentStatus === "error" ? "Sessão não encontrada" : "Tempo esgotado"}
          </h2>
          <p className="text-muted-foreground mb-6">
            {paymentStatus === "timeout"
              ? "Não foi possível confirmar o pagamento. Se o valor foi cobrado, entre em contato."
              : paymentStatus === "error"
              ? "Verifique se o ID da sessão está correto e tente novamente."
              : "A sessão de pagamento expirou. Tente novamente."}
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => { setPaymentStatus("no_session"); setSessionId(""); }} data-testid="try-again-btn">
              Tentar Novamente
            </Button>
            <Button onClick={() => navigate("/")} data-testid="back-home-btn">
              Voltar ao Início
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // State: paid → show registration form
  return (
    <div className="min-h-screen flex items-center justify-center bg-background noise-bg p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
            <Wallet className="w-6 h-6 text-primary-foreground" weight="fill" />
          </div>
          <span className="font-heading font-extrabold text-2xl tracking-tight text-primary">
            Financeiro
          </span>
        </div>

        <div className="flex items-center gap-2 mb-6 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
          <CheckCircle size={24} className="text-green-500" weight="fill" />
          <span className="text-green-700 dark:text-green-400 font-medium text-sm">
            Pagamento confirmado! Crie sua conta para acessar.
          </span>
        </div>

        <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
          Criar sua conta
        </h1>
        <p className="text-muted-foreground mb-8">
          Preencha os dados abaixo para ativar seu acesso vitalício.
        </p>

        <form onSubmit={handleRegister} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              type="text"
              placeholder="Seu nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="h-12"
              data-testid="checkout-register-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-12"
              data-testid="checkout-register-email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Min. 6 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="h-12 pr-12"
                data-testid="checkout-register-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          <Button
            type="submit"
            className="w-full h-12 text-base font-medium tracing-beam"
            disabled={loading}
            data-testid="checkout-register-submit"
          >
            {loading ? "Criando conta..." : "Ativar Meu Acesso"}
          </Button>
        </form>

        <p className="text-xs text-muted-foreground mt-6 text-center">
          Acesso vitalício. Sem mensalidades. Sem limites.
        </p>
      </div>
    </div>
  );
}
